import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) =>
  new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

// Smart "best day" chips for scheduling a job — ranks upcoming days by how
// close the address sits to already-scheduled work on that day.
export const DaySuggestions = ({ address, latitude, longitude, customerId, value, onPick }) => {
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(false);

  // Owner/admin-only feature — workers never see round strategy
  let isWorker = false;
  try {
    const u = JSON.parse(localStorage.getItem('streakfree_user') || 'null');
    isWorker = u?.role === 'Worker';
  } catch { /* treat as non-worker; backend enforces 403 anyway */ }

  useEffect(() => {
    if (isWorker) return;
    if (!address && latitude == null && !customerId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/optimizer/place-customer`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ address, latitude, longitude, customer_id: customerId })
        });
        if (!res.ok) throw new Error('place-customer failed');
        const data = await res.json();
        if (!cancelled) setSuggestions(data.resolved ? data.suggestions : []);
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [address, latitude, longitude, customerId]);

  if (isWorker) return null;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2.5" data-testid="day-suggestions-loading">
        <Loader2 size={14} className="animate-spin" />
        Finding the best day for this address…
      </div>
    );
  }

  if (!suggestions || suggestions.length === 0) return null;

  return (
    <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3" data-testid="day-suggestions">
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles size={14} className="text-indigo-600" />
        <span className="text-[11px] font-semibold text-indigo-800 uppercase tracking-wide">
          Best days for this address
        </span>
      </div>
      <div className="space-y-1.5">
        {suggestions.slice(0, 3).map((s) => {
          const selected = value === s.date;
          return (
            <button
              key={s.date}
              type="button"
              onClick={() => onPick(s.date)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-left transition-colors ${
                selected
                  ? 'bg-indigo-600 border-indigo-600 text-white'
                  : 'bg-white border-indigo-200 text-zinc-800 hover:bg-indigo-100'
              }`}
              data-testid={`day-suggestion-${s.date}`}
            >
              <span className="font-semibold text-sm whitespace-nowrap">{fmtDate(s.date)}</span>
              <span className={`text-xs truncate ${selected ? 'text-indigo-100' : 'text-zinc-500'}`}>
                {s.nearest_mi} mi from {s.nearest_customer_name}
                {s.nearby_count > 1 ? ` · ${s.nearby_count} jobs nearby` : ''}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DaySuggestions;
